import { Upload, Loader2, AlertCircle } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { ConfirmDialog } from "@/shared/components/ui/ConfirmDialog";
import { useEvaluacion } from "@/features/evaluacion";
import type { EvaluacionContext } from "@/features/evaluacion";

interface EvaluacionDropzoneProps {
  context: EvaluacionContext;
  intentosUsados: number;
  onEvaluacionComplete?: (retroContent: string, sessionId: string) => void;
  onFileSelected?: (file: File) => void;
  colorPrimario?: string;
}
const COLORS = {
  primary: "#5b3bea", // morado
  yellow: "#f4c430",
  border: "#e5e7eb",
  bg: "#f3f4f6",
  success: "#10b981",
  error: "#ef4444",
};
function getColorValue(color: string): string {
  if (color.startsWith("var(")) {
    const root = document.documentElement;
    const varName = color.match(/var\(([^)]+)\)/)?.[1];
    if (varName) {
      const computed = getComputedStyle(root).getPropertyValue(varName.trim());
      return computed.trim() || COLORS.primary;
    }
  }
  return color;
}

export default function EvaluacionDropzone({
  context,
  intentosUsados,
  onEvaluacionComplete,
  onFileSelected,
  colorPrimario = COLORS.primary,
}: EvaluacionDropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [_file, setFile] = useState<File | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [_retroContent, setRetroContent] = useState<string | null>(null);
  const [colorValue, setColorValue] = useState<string>(colorPrimario);
  useEffect(() => {
    const value = getColorValue(colorPrimario);
    setColorValue(value);
  }, [colorPrimario]);
  const { isProcessing, error, evaluarArchivo, canEvaluate } = useEvaluacion({
    context,
    callbacks: {
      onComplete: (result) => {
        setRetroContent(result.evaluationResult.base_feedback);
        onEvaluacionComplete?.(
          result.evaluationResult.base_feedback,
          result.evaluationResult.session_id,
        );
      },
      onError: (err) => {
        console.error("Error en evaluación:", err);
      },
    },
  });
  const handleFiles = (f: FileList | null) => {
    setValidationError(null);
    setRetroContent(null);
    if (!f || !f[0]) return;
    const candidate = f[0];
    if (
      candidate.type !== "application/pdf" &&
      !candidate.name.toLowerCase().endsWith(".pdf")
    ) {
      setValidationError("Solo se permite PDF.");
      return;
    }
    setPendingFile(candidate);
    setShowConfirm(true);
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    if (!canEvaluate(pendingFile, intentosUsados)) {
      setShowConfirm(false);
      setPendingFile(null);
      return;
    }
    setFile(pendingFile);
    setShowConfirm(false);
    if (onFileSelected) {
      onFileSelected(pendingFile);
    } else {
      await evaluarArchivo(pendingFile);
    }
    setPendingFile(null);
  };
  const limiteAlcanzado = intentosUsados >= context.limiteIntentos;
  const puedeSubir = !isProcessing && !limiteAlcanzado;

  return (
    <div className="max-w-5xl mx-auto w-full space-y-6">
      <ConfirmDialog
        open={showConfirm}
        title="¿Confirmar archivo?"
        description={
          pendingFile ? `Vas a subir el archivo: ${pendingFile.name}` : ""
        }
        confirmText="Sí, subir archivo"
        cancelText="Cancelar"
        onConfirm={confirmUpload}
        onCancel={() => {
          setShowConfirm(false);
          setPendingFile(null);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
      {/* Dropzone */}
      <div
        role="button"
        tabIndex={puedeSubir ? 0 : -1}
        onClick={() => {
          if (puedeSubir) inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (puedeSubir && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          if (puedeSubir) {
            e.preventDefault();
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (puedeSubir) {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }
        }}
        className={`rounded-3xl p-10 md:p-14 shadow-xl relative transition-all ${
          puedeSubir
            ? "cursor-pointer hover:scale-[1.02] bg-[var(--card-background)] dark:bg-[var(--card-background)]"
            : "cursor-not-allowed opacity-60 bg-[var(--background-muted)] dark:bg-[var(--background-muted)]"
        }`}
        style={{
          outline: `3px dashed color-mix(in srgb, ${limiteAlcanzado ? COLORS.error : colorValue} 60%, transparent)`,
          outlineOffset: 0,
          boxShadow: dragOver
            ? `0 20px 60px ${colorValue}40`
            : "0 10px 40px rgba(0,0,0,.08)",
        }}
      >
        <div
          className={`absolute inset-4 rounded-2xl border`}
          style={{
            backgroundColor: "var(--card-background)",
            borderColor: limiteAlcanzado
              ? COLORS.error
              : "color-mix(in srgb, var(--secondary) 50%, transparent)",
          }}
        />
        <div className="relative z-10 flex flex-col items-center text-center gap-3">
          {/* Icono de carga */}
          <div className="w-16 h-16 flex items-center justify-center mb-2">
            <span
              className="flex items-center justify-center w-16 h-16 rounded-full"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--secondary) 20%, transparent)",
              }}
            >
              <Upload className="text-[var(--primary)]" size={36} />
            </span>
          </div>
          {/* Título dropzone */}
          <p className="text-lg md:text-xl text-[var(--foreground)] font-semibold">
            Haz click o arrastra tu archivo aquí
          </p>
          {/* Subtítulo formatos */}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            PDF (Máx. 10MB)
          </p>
          {/* Nota */}
          <p className="text-xs italic text-gray-400 dark:text-gray-300 mb-2">
            Aquí puedes subir tu primer reto contestado para recibir
            retroalimentación y conversar con tu coach ia, asegúrate de utilizar{" "}
            {context.limiteIntentos === 1
              ? "tu único intento"
              : `tus ${context.limiteIntentos} intentos`}{" "}
            en tu mejor reto contestado.
          </p>
          {isProcessing ? (
            // Vista cuando está procesando
            <>
              <div className="w-20 h-20 rounded-full flex items-center justify-center">
                <Loader2
                  className="animate-spin"
                  style={{ color: colorValue }}
                  size={48}
                />
              </div>
              <p className="text-2xl md:text-3xl text-[var(--foreground)] font-bold">
                Preparando tu retroalimentación
              </p>
              <p className="text-base text-[var(--foreground)] opacity-70">
                Por favor espera mientras analizamos tu archivo...
              </p>
            </>
          ) : (
            <>{/* Resto del contenido del dropzone */}</>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={!puedeSubir}
          />
        </div>
      </div>
      {/* Alerta de error */}
      {(validationError || (error && !onFileSelected)) && (
        <div className="flex justify-center mb-4">
          <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg px-4 py-2 shadow font-medium">
            <AlertCircle size={18} className="text-red-500 dark:text-red-300" />
            {validationError || error}
          </div>
        </div>
      )}

    </div>
  );
}
